// X56 (Grant 2026-10-19: "the breadcrumbs are not right on this page, check other pages too"): the frontend
// trail for every non-admin page, in one place and testable without a browser.
//
// A page's trail is Home plus what its route name says (TRAILS below, one line per newer route), or, for a route
// without an entry, what the route-name logic that was in FrontendBreadcrumbs.vue builds (legacyTrail, moved here
// unchanged so nothing that worked keeps working differently). A page can also hand the component an explicit
// `items` list (declarative, after Home) or a `finalBreadcrumb` that names the current page when the trail can't
// (the membership type page: the type's name).
//
// Entity names come from what the page already passes to <frontend-header> (club, league, ladder, …). The club link
// uses the slug when there is one, as the club page's own crumb does.
import _ from 'lodash';
import {DateTime} from 'luxon';

/** The profile page's tabs (?tab=) by their query value: the second crumb under "Your profile". `basic` is the page itself. */
export const PROFILE_TAB_TITLES = {
  services: 'External services',
  notifications: 'Notifications',
  payments: 'Payments',
  memberships: 'Memberships & passes',
  bookings: 'Bookings',
  loginMethods: 'Sign in & security',
  appSettings: 'App Settings',
  privacy: 'Privacy',
  following: 'Following',
  blocked: 'Blocked users',
  delete: 'Delete account',
};

export const HOME_CRUMB = {title: 'Home', to: '/'};

const clubKey = (club) => club?.slug ?? club?.id;

/** The club crumb: a link to the club page unless the club page is where we are. */
export function clubCrumb(club, {here = false} = {}) {
  if (!club?.name) return null;
  return {title: club.name, disabled: here, to: {name: 'ClubDetail', params: {clubId: clubKey(club)}}};
}

const here = (title) => ({title, disabled: true});
const link = (title, to) => ({title, disabled: false, to});

/**
 * The match crumbs (club › competition › Match), exactly as FrontendBreadcrumbs built them for the match page.
 * `matchTo` links the Match crumb instead of ending the trail on it (the live scoring page).
 */
export function fixtureCrumbs(fixture, routes, {matchTo = null} = {}) {
  const breadcrumbs = [];
  if (!fixture) return breadcrumbs;
  // Add club breadcrumb
  if (fixture.clubId && fixture.clubName) {
    let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
    clubRoute = clubRoute.replace(":clubId", fixture.clubSlug);
    breadcrumbs.push({title: fixture.clubName, disabled: false, to: clubRoute});
  }

  // Add ladder or league breadcrumb
  if (fixture.ladderId && fixture.ladderName) {
    // Ladder fixture
    let ladderRoute = routes.find(r => r.name === 'LadderDetail').path;
    ladderRoute = ladderRoute.replace(":ladderId", fixture.ladderId);
    breadcrumbs.push({title: fixture.ladderName, disabled: false, to: ladderRoute});
  } else if (fixture.leagueCycleDivisionId && fixture.leagueCycleDivisionName) {
    // League fixture
    let leagueRoute = routes.find(r => r.name === 'LeagueDetail').path;
    leagueRoute = leagueRoute.replace(":leagueId", fixture.leagueId);
    leagueRoute = leagueRoute.replace(":cycleId?", fixture.leagueCycleId || '');
    breadcrumbs.push({
      title: fixture.leagueName,
      disabled: false,
      to: {
        path: leagueRoute,
        hash: `#division-${fixture.leagueCycleDivisionId}`
      }
    });

  } else if (fixture.tournamentId && fixture.tournamentName) {
    // Tournament match (X61, R23b L5: the one format whose match had no way back to its competition)
    breadcrumbs.push({
      title: fixture.tournamentName,
      disabled: false,
      to: {name: 'TournamentDetail', params: {tournamentId: fixture.tournamentId}}
    });
  } else if (fixture.americanoCycleDivisionId && fixture.americanoName) {
    // Americano match — link back to the americano league's cycle
    breadcrumbs.push({
      title: fixture.americanoName,
      disabled: false,
      to: {
        name: 'AmericanoDetail',
        params: {americanoId: fixture.americanoId, cycleId: fixture.americanoCycleId}
      }
    });
  } else if (fixture.meetOccurrenceId && fixture.meetId) {
    // Meet match — the meet, then its date (a meet match is scheduled ON the date)
    breadcrumbs.push({
      title: fixture.meetName || 'Meet',
      disabled: false,
      to: {name: 'MeetDetail', params: {meetId: fixture.meetId}}
    });
    breadcrumbs.push({
      title: fixture.scheduledAt
          ? DateTime.fromISO(fixture.scheduledAt, {zone: 'utc'}).toLocal().toFormat('ccc d LLL')
          : 'Date',
      disabled: false,
      to: {name: 'MeetOccurrenceDetail', params: {meetId: fixture.meetId, occurrenceId: fixture.meetOccurrenceId}}
    });
  }

  // Add fixture breadcrumb
  breadcrumbs.push(matchTo ? {title: "Match", disabled: false, to: matchTo} : {title: "Match", disabled: true});
  return breadcrumbs;
}

/**
 * The newer pages, one line each: route name → the crumbs after Home. `props` is what the page gave the header
 * (club, tournament, fixture, …), `route` the current route, `routes` the router's route table.
 */
export const TRAILS = {
  // Home › {club} › Book
  ClubBook: ({props}) => [clubCrumb(props.club), here('Book')],
  // Home › {club} › Memberships & passes
  ClubMembershipsJoin: ({props}) => [clubCrumb(props.club), here('Memberships & passes')],
  // Home › {club} › Memberships & passes › {type}   (the type's name arrives as finalBreadcrumb)
  ClubMembershipJoin: ({props, route}) => [
    clubCrumb(props.club),
    link('Memberships & passes', {name: 'ClubMembershipsJoin', params: {clubId: clubKey(props.club) ?? route.params.clubId}}),
    here('Membership'),
  ],
  // Home › {club} › {tournament} › Stats   (TournamentDetail's own block never matched this route)
  TournamentStats: ({props}) => props.tournament ? [
    clubCrumb(props.tournament.club ?? props.club),
    link(props.tournament.name, {name: 'TournamentDetail', params: {tournamentId: props.tournament.id}}),
    here('Stats'),
  ] : [],
  // Home › {club} › {competition} › Match › Live scoring
  FixtureLiveScoring: ({props, routes}) => props.fixture ? [
    ...fixtureCrumbs(props.fixture, routes, {matchTo: {name: 'FixtureDetail', params: {fixtureId: props.fixture.id}}}),
    here('Live scoring'),
  ] : [here('Live scoring')],
  // Home › Your profile › {tab}
  YourProfile: ({route}) => {
    const tab = Array.isArray(route.query?.tab) ? route.query.tab[0] : route.query?.tab;
    const title = tab && PROFILE_TAB_TITLES[tab];
    return title ? [link('Your profile', {name: 'YourProfile'}), here(title)] : [here('Your profile')];
  },
  // Home › Help › Categories   (was Home › Categories, with Categories a link to Help)
  HowItWorksCategories: () => [link('Help', {name: 'HowItWorks'}), here('Categories')],
  // Home › {club} › {player}   (X61, R23b L3; Grant: no "Clubs" crumb. The page's first club; a player with no club: Home › {player})
  PlayerDetail: ({props}) => props.user ? [clubCrumb(props.club), here(props.user.fullName)] : [],
};

/**
 * The route-name logic FrontendBreadcrumbs.vue carried until X56, unchanged: the fallback for every route without
 * a TRAILS entry. Returns the whole trail, Home first.
 */
export function legacyTrail({route, routes, props}) {
  const breadcrumbs = [
    {
      title: 'Home',
      disabled: route.name === 'Home',
      to: '/',
    },
  ];

  if (_.some(route.matched, r => _.includes(['Contact'], r.name))) {
    breadcrumbs.push({title: "Contact", disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['ClubSearch'], r.name))) {
    breadcrumbs.push({title: "Search clubs", disabled: true});
  }

  if (_.some(route.matched, r => _.includes(['HowToView', 'HowItWorks'], r.name)) && props.howTo) {
    breadcrumbs.push({title: "Help", disabled: false, to: {name: 'HowItWorks'}});
  }
  if (_.some(route.matched, r => _.includes(['HowToView'], r.name)) && props.howTo) {
    breadcrumbs.push({title: props.howTo.title, disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['HowItWorksCategories'], r.name))) {
    breadcrumbs.push({title: "Categories", disabled: route.name === 'HowItWorksCategories', to: {name: 'HowItWorks'}});
  }
  if (_.some(route.matched, r => _.includes(['HowToCategoryAdd', 'HowToCategoryEdit', 'HowToCategoryDelete'], r.name))) {
    breadcrumbs.push({
      title: route.name === 'HowToCategoryEdit' ? 'Edit' : route.name === 'HowToCategoryDelete' ? 'Delete' : 'Add new',
      disabled: true
    });
  }
  if (_.some(route.matched, r => _.includes(['HowToAdd', 'HowToEdit', 'HowToDelete'], r.name))) {
    breadcrumbs.push({
      title: route.name === 'HowToEdit' ? 'Edit' : route.name === 'HowToDelete' ? 'Delete' : 'Add new',
      disabled: true
    });
  }
  if (_.some(route.matched, r => _.includes(['HowItWorks'], r.name))) {
    breadcrumbs.push({title: "Help", disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['About'], r.name))) {
    breadcrumbs.push({title: "About", disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['YourProfile'], r.name))) {
    breadcrumbs.push({title: "Your profile", disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['PlayerDetail'], r.name) && props.user)) {
    breadcrumbs.push({title: props.user.fullName, disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['PlayerStats'], r.name) && props.user)) {
    breadcrumbs.push({
      title: props.user.fullName,
      disabled: false,
      to: {name: 'PlayerDetail', params: {playerId: props.user.id}}
    });
    breadcrumbs.push({title: 'Stats', disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['FixtureSearch'], r.name))) {
    breadcrumbs.push({title: 'Match search', disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['ClubDetail'], r.name)) && props.club) {
    let routePath = routes.find(r => r.name === 'ClubDetail').path;
    routePath = routePath.replace(":clubId", props.club.slug ?? props.club.id);
    breadcrumbs.push({title: props.club.name, disabled: route.name === 'ClubDetail', to: routePath});
  }
  if (_.some(route.matched, r => _.includes(['ClubStats'], r.name)) && props.club) {
    let routePath = routes.find(r => r.name === 'ClubDetail').path;
    routePath = routePath.replace(":clubId", props.club.slug ?? props.club.id);
    breadcrumbs.push({title: props.club.name, disabled: route.name === 'ClubDetail', to: routePath});
    breadcrumbs.push({title: 'Stats', disabled: true});
  }

  if (_.some(route.matched, r => _.includes(['LeagueDetail', 'LeagueCycleStats'], r.name)) && props.league) {
    if (props.league.clubId && props.league.clubName) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.league.clubSlug);
      breadcrumbs.push({title: props.league.clubName, disabled: route.name === 'ClubDetail', to: clubRoute});
    }
    let leagueRoute = routes.find(r => r.name === 'LeagueDetail').path;
    leagueRoute = leagueRoute.replace(":leagueId", props.league.id);
    // X61 (R23b M3): the optional cycle segment is filled with the cycle whose stats are shown, else dropped —
    // the literal "/:cycleId?" used to go out as the href.
    const cycleId = route.name === 'LeagueCycleStats' ? route.params?.cycleId : null;
    leagueRoute = leagueRoute.replace("/:cycleId?", cycleId ? `/${cycleId}` : '');
    breadcrumbs.push({title: props.league.name, disabled: route.name === 'LeagueDetail', to: leagueRoute});
    if (route.name === 'LeagueCycleStats') {
      breadcrumbs.push({title: 'Stats', disabled: true});
    }
  }
  if (_.some(route.matched, r => _.includes(['LadderDetail', 'LadderStats'], r.name)) && props.ladder) {
    if (props.ladder.clubId && props.ladder.clubName) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.ladder.clubSlug);
      breadcrumbs.push({title: props.ladder.clubName, disabled: route.name === 'ClubDetail', to: clubRoute});
    }
    let ladderRoute = routes.find(r => r.name === 'LadderDetail').path;
    ladderRoute = ladderRoute.replace(":ladderId", props.ladder.id);
    breadcrumbs.push({title: props.ladder.name, disabled: route.name === 'LadderDetail', to: ladderRoute});
    if (route.name === 'LadderStats') {
      breadcrumbs.push({title: 'Stats', disabled: true});
    }
  }
  if (_.some(route.matched, r => _.includes(['LadderChallengeDetail'], r.name)) && props.ladderChallenge) {
    if (props.ladderChallenge.clubId && props.ladderChallenge.clubName) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.ladderChallenge.clubSlug);
      breadcrumbs.push({title: props.ladderChallenge.clubName, disabled: route.name === 'ClubDetail', to: clubRoute});
    }
    let ladderRoute = routes.find(r => r.name === 'LadderDetail').path;
    ladderRoute = ladderRoute.replace(":ladderId", props.ladderChallenge.ladderId);
    breadcrumbs.push({title: props.ladderChallenge.ladderName, disabled: false, to: ladderRoute});
    breadcrumbs.push({title: "Challenge", disabled: true});
  }
  if (_.some(route.matched, r => _.includes(['TournamentDetail'], r.name)) && props.tournament) {
    if (props.tournament.clubId && props.tournament.club?.name) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.tournament.club.slug);
      breadcrumbs.push({title: props.tournament.club.name, disabled: false, to: clubRoute});
    }
    let tournamentRoute = routes.find(r => r.name === 'TournamentDetail').path;
    tournamentRoute = tournamentRoute.replace(":tournamentId", props.tournament.id);
    breadcrumbs.push({title: props.tournament.name, disabled: route.name === 'TournamentDetail', to: tournamentRoute});
  }
  if (_.some(route.matched, r => _.includes(['MeetDetail', 'MeetStats', 'MeetOccurrenceDetail'], r.name)) && props.meet) {
    if (props.meet.clubId && props.meet.clubName) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.meet.clubSlug ?? props.meet.clubId);
      breadcrumbs.push({title: props.meet.clubName, disabled: false, to: clubRoute});
    }
    let meetRoute = routes.find(r => r.name === 'MeetDetail').path;
    meetRoute = meetRoute.replace(":meetId", props.meet.id);
    breadcrumbs.push({title: props.meet.name, disabled: route.name === 'MeetDetail', to: meetRoute});
    if (route.name === 'MeetStats') {
      breadcrumbs.push({title: 'Stats', disabled: true});
    }
    if (route.name === 'MeetOccurrenceDetail' && props.meetOccurrence) {
      breadcrumbs.push({
        title: DateTime.fromISO(props.meetOccurrence.startsAt, {zone: 'utc'}).toLocal().toFormat('ccc d LLL'),
        disabled: true
      });
    }
  }
  if (_.some(route.matched, r => _.includes(['AmericanoDetail', 'AmericanoStats'], r.name)) && props.americano) {
    if (props.americano.clubId && props.americano.clubName) {
      let clubRoute = routes.find(r => r.name === 'ClubDetail').path;
      clubRoute = clubRoute.replace(":clubId", props.americano.clubSlug ?? props.americano.clubId);
      breadcrumbs.push({title: props.americano.clubName, disabled: false, to: clubRoute});
    }
    let americanoRoute = routes.find(r => r.name === 'AmericanoDetail').path;
    americanoRoute = americanoRoute.replace(":americanoId", props.americano.id);
    americanoRoute = americanoRoute.replace("/:cycleId?", "");
    breadcrumbs.push({
      title: props.americano.name,
      disabled: route.name === 'AmericanoDetail' && !props.americanoCycle,
      to: americanoRoute
    });
    if (route.name === 'AmericanoStats') {
      breadcrumbs.push({title: 'Stats', disabled: true});
    }
    if (route.name === 'AmericanoDetail' && props.americanoCycle) {
      breadcrumbs.push({title: props.americanoCycle.name, disabled: true});
    }
  }
  if (_.some(route.matched, r => _.includes(['FixtureDetail'], r.name)) && props.fixture) {
    breadcrumbs.push(...fixtureCrumbs(props.fixture, routes));
  }

  return breadcrumbs;
}

/**
 * The whole trail for the current page, Home first, the last crumb the page itself (never a link).
 *
 * @param route   the current route (name, params, query, matched)
 * @param routes  router.getRoutes()
 * @param props   what the page gave the header: club, league, ladder, ladderChallenge, fixture, tournament, meet,
 *                meetOccurrence, americano, americanoCycle, user, howTo
 * @param items   an explicit trail after Home ({title, to?, disabled?} each); wins over everything
 * @param final   a name for the current page that replaces the last crumb's title
 */
export function frontendTrail({route, routes = [], props = {}, items = null, final = null}) {
  let trail;
  if (Array.isArray(items) && items.length) {
    trail = [HOME_CRUMB, ...items.map((c) => ({title: c.title, disabled: !!c.disabled || !c.to, to: c.to}))];
  } else if (TRAILS[route?.name]) {
    trail = [HOME_CRUMB, ...TRAILS[route.name]({route, routes, props}).filter(Boolean)];
  } else {
    trail = legacyTrail({route, routes, props});
  }
  if (trail.length > 1) {
    const last = trail[trail.length - 1];
    // The current page is never a link; `final` names it (never Home).
    trail[trail.length - 1] = {...last, title: final || last.title, disabled: true};
  }
  return trail;
}
